import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCategorias } from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type { Categoria } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const NINGUNO = '__ninguno__';

function aplanarCategorias(cats: Categoria[], nivel = 0): Array<Categoria & { nivel: number }> {
  const result: Array<Categoria & { nivel: number }> = [];
  for (const c of cats) {
    result.push({ ...c, nivel });
    if (c.hijos?.length) result.push(...aplanarCategorias(c.hijos, nivel + 1));
  }
  return result;
}

function DialogNuevaCategoria({
  categorias,
  open,
  onClose,
}: {
  categorias: Categoria[];
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [padreId, setPadreId] = useState(NINGUNO);
  const [guardando, setGuardando] = useState(false);
  const catsAplanas = aplanarCategorias(categorias);

  const handleGuardar = async () => {
    if (!nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setGuardando(true);
    try {
      const res = await cliente.categorias.crear(
        nombre.trim(),
        padreId !== NINGUNO ? Number(padreId) : null,
      );
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Categoría creada');
      qc.invalidateQueries({ queryKey: ['categorias'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al crear');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Nueva categoría</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Nombre *</Label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre de la categoría"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleGuardar()}
            />
          </div>
          <div className="space-y-1">
            <Label>Categoría padre (opcional)</Label>
            <Select value={padreId} onValueChange={setPadreId}>
              <SelectTrigger>
                <SelectValue placeholder="Sin padre — categoría raíz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NINGUNO}>Sin padre (raíz)</SelectItem>
                {catsAplanas.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {'  '.repeat(c.nivel)}
                    {c.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilaCategoria({ cat, nivel }: { cat: Categoria; nivel: number }) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(cat.nombre);
  const [guardando, setGuardando] = useState(false);

  const handleGuardar = async () => {
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      const res = await cliente.categorias.actualizar(cat.id, nombre.trim());
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Categoría actualizada');
      setEditando(false);
      qc.invalidateQueries({ queryKey: ['categorias'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async () => {
    if (!confirm(`¿Eliminar "${cat.nombre}"?`)) return;
    try {
      const res = await cliente.categorias.eliminar(cat.id);
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Categoría eliminada');
      qc.invalidateQueries({ queryKey: ['categorias'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al eliminar');
    }
  };

  const esRaiz = nivel === 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 pr-2 group ${esRaiz ? 'bg-muted/40 border-b font-semibold' : 'border-b border-border/40 hover:bg-muted/20'}`}
        style={{ paddingLeft: `${12 + nivel * 20}px` }}
      >
        {nivel > 0 && <span className="text-muted-foreground/40 text-xs shrink-0">└─</span>}

        {editando ? (
          <div className="flex items-center gap-1 flex-1">
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="h-7 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGuardar();
                if (e.key === 'Escape') {
                  setEditando(false);
                  setNombre(cat.nombre);
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-green-600"
              onClick={handleGuardar}
              disabled={guardando}
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                setEditando(false);
                setNombre(cat.nombre);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <>
            <span className={`flex-1 text-sm ${esRaiz ? 'text-xs uppercase tracking-wide' : ''}`}>
              {cat.nombre}
            </span>
            <span className="text-xs text-muted-foreground/40 tabular-nums mr-1">#{cat.id}</span>
            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setEditando(true)}
              >
                <Edit className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive hover:text-destructive"
                onClick={handleEliminar}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </>
        )}
      </div>
      {cat.hijos?.map((hijo) => (
        <FilaCategoria key={hijo.id} cat={hijo} nivel={nivel + 1} />
      ))}
    </div>
  );
}

function buscarEnArbol(cats: Categoria[], texto: string): Categoria[] {
  const t = texto.toLowerCase();
  const resultado: Categoria[] = [];
  for (const c of cats) {
    const hijosMatch = buscarEnArbol(c.hijos ?? [], texto);
    const propio = c.nombre.toLowerCase().includes(t);
    if (propio) {
      resultado.push({ ...c, hijos: c.hijos ?? [] });
    } else if (hijosMatch.length > 0) {
      resultado.push({ ...c, hijos: hijosMatch });
    }
  }
  return resultado;
}

export function CategoriasPantalla() {
  const { data: categorias, isLoading } = useCategorias();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const total = (cats: Categoria[]): number =>
    cats.reduce((n, c) => n + 1 + total(c.hijos ?? []), 0);

  const filtradas = busqueda.trim()
    ? buscarEnArbol(categorias ?? [], busqueda.trim())
    : (categorias ?? []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Categorías</h1>
          <p className="text-sm text-muted-foreground">
            {categorias ? `${total(categorias)} categorías` : 'Cargando...'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="Buscar categoría..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-52"
          />
          <Button className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" /> Nueva
          </Button>
        </div>
      </div>

      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
        Algunas entradas pueden ser filas de resumen importadas del Excel por error (ej: "BANCOS
        COLONES", "TOTAL PAGOS COLONIZADO"). Podés eliminarlas — el sistema protege las que tienen
        conceptos asociados.
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Cargando...</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {filtradas.map((cat) => (
            <FilaCategoria key={cat.id} cat={cat} nivel={0} />
          ))}
          {filtradas.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">
              {busqueda ? 'Sin resultados para esa búsqueda.' : 'Sin categorías'}
            </div>
          )}
        </div>
      )}

      {categorias && (
        <DialogNuevaCategoria
          key={dialogOpen ? 'open' : 'closed'}
          categorias={categorias}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}
